import React, { useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { InventoryDashboard } from './inventory/Dashboard';
import { InventoryStatus } from './inventory/InventoryStatus';
import { PurchaseOrders } from './inventory/PurchaseOrders';
import { InventoryReports } from './inventory/InventoryReports';
import { InventoryItems } from './inventory/InventoryItems';
import { Transactions } from './inventory/Transactions';

/**
 * Phase 1a Inventory UI entry point.
 */
const Inventory: React.FC = () => {
  const tabs = useMemo(() => ([
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'inventory-status', label: 'Inventory Status' },
    { id: 'inventory-items', label: 'Inventory Items' },
    { id: 'counts', label: 'Counts' },
    { id: 'purchase-orders', label: 'Purchase Orders' },
    { id: 'reports', label: 'Reports' },
    { id: 'transactions', label: 'Transactions' },
  ]), []);

  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') || 'inventory-status';
  const allowedTabs = useMemo(() => new Set(tabs.map(t => t.id)), [tabs]);
  const activeTab = allowedTabs.has(tabFromUrl) ? tabFromUrl : 'inventory-status';

  // If tab param is missing/invalid, normalize the URL (replace so it doesn't add history noise).
  useEffect(() => {
    if (!allowedTabs.has(tabFromUrl)) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', 'inventory-status');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabFromUrl, allowedTabs]);

  const setActiveTab = (tabId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tabId);
    setSearchParams(next); // push history so Back/Forward works
  };

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Inventory Management</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage inventory stock levels and purchase orders.
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                  ${activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        {activeTab === 'dashboard' ? (
          <InventoryDashboard />
        ) : activeTab === 'inventory-status' ? (
          <InventoryStatus />
        ) : activeTab === 'inventory-items' ? (
          <InventoryItems />
        ) : activeTab === 'purchase-orders' ? (
          <PurchaseOrders />
        ) : activeTab === 'reports' ? (
          <InventoryReports />
        ) : activeTab === 'transactions' ? (
          <Transactions />
        ) : (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <div className="text-sm text-gray-600">
              This tab is planned for a later sprint.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Inventory;


